/**
 * CaseBuilder — builder visual de casos clínicos para profesores.
 *
 * Sin JSON en ninguna parte. El docente médico completa un formulario
 * estructurado por secciones (datos del paciente → hallazgos → publicar)
 * y el builder genera el PatientGraph internamente. Incluye catálogos de
 * conceptos (LOINC/SNOMED) preseleccionados para vitales, labs, síntomas,
 * imágenes y diagnósticos comunes, así el profesor sólo elige del dropdown
 * y ajusta valores. Si necesita algo custom, usa "Otro" y completa campos.
 *
 * Pipeline del caso:
 *   UI form ──→ BuilderState ──→ buildPatientGraph ──→ caseRepository.createRemote
 *
 * La validación del schema sigue siendo la del backend; este módulo solo
 * garantiza que lo que arma sea válido estructuralmente (campos obligatorios
 * completos antes de permitir "Publicar").
 */

import type {
  AnatomicalSystem,
  ClinicalNodeData,
  NodeType,
  PatientGraph,
  TriagePriority,
} from '../types/medical';

export type BuilderSeverity = 'mild' | 'moderate' | 'severe' | 'life-threatening';

export interface BuilderFinding {
  uiId: string;
  type: Exclude<NodeType, 'complaint' | 'allergy' | 'treatment' | 'physical_exam'>;
  conceptDisplay: string;
  conceptCode?: string;
  conceptSystem?: string;
  anatomicalSystem?: AnatomicalSystem;
  // Numérico (vitales / labs)
  numericValue?: number;
  unit?: string;
  refLow?: number;
  refHigh?: number;
  // Texto (síntomas / imágenes / descripción libre)
  textValue?: string;
  severity?: BuilderSeverity;
}

export interface BuilderState {
  slug: string;
  displayLabel: string;
  age: number;
  sex: 'male' | 'female' | 'other';
  weight?: number;
  chiefComplaint: string;
  triagePriority: TriagePriority;
  findings: BuilderFinding[];
}

// ═══════════════════════════════════════════════════════════════════
// Catálogos de conceptos preseleccionados — LOINC / SNOMED CT
// ═══════════════════════════════════════════════════════════════════

export interface Preset {
  key: string;
  display: string;
  code: string;
  system: string; // URL del vocabulario (LOINC/SNOMED)
  unit?: string;
  refLow?: number;
  refHigh?: number;
  anatomicalSystem: AnatomicalSystem;
  defaultSeverity?: BuilderSeverity;
}

export const VITAL_PRESETS: Preset[] = [
  { key: 'fc', display: 'Frecuencia cardíaca', code: '8867-4', system: 'http://loinc.org', unit: 'bpm', refLow: 60, refHigh: 100, anatomicalSystem: 'cardiovascular' },
  { key: 'pas', display: 'Presión arterial sistólica', code: '8480-6', system: 'http://loinc.org', unit: 'mmHg', refLow: 90, refHigh: 140, anatomicalSystem: 'cardiovascular' },
  { key: 'pad', display: 'Presión arterial diastólica', code: '8462-4', system: 'http://loinc.org', unit: 'mmHg', refLow: 60, refHigh: 90, anatomicalSystem: 'cardiovascular' },
  { key: 'spo2', display: 'SpO₂', code: '59408-5', system: 'http://loinc.org', unit: '%', refLow: 95, refHigh: 100, anatomicalSystem: 'respiratory' },
  { key: 'temp', display: 'Temperatura', code: '8310-5', system: 'http://loinc.org', unit: '°C', refLow: 36, refHigh: 37.5, anatomicalSystem: 'infectious' },
  { key: 'fr', display: 'Frecuencia respiratoria', code: '9279-1', system: 'http://loinc.org', unit: 'rpm', refLow: 12, refHigh: 20, anatomicalSystem: 'respiratory' },
  { key: 'glicemia-cap', display: 'Glicemia capilar', code: '2339-0', system: 'http://loinc.org', unit: 'mg/dL', refLow: 70, refHigh: 140, anatomicalSystem: 'metabolic' },
  { key: 'glasgow', display: 'Escala de Glasgow', code: '9269-2', system: 'http://loinc.org', unit: 'pts', refLow: 15, refHigh: 15, anatomicalSystem: 'neurological' },
];

export const LAB_PRESETS: Preset[] = [
  { key: 'troponina-i', display: 'Troponina I', code: '10839-9', system: 'http://loinc.org', unit: 'ng/mL', refLow: 0, refHigh: 0.04, anatomicalSystem: 'cardiovascular', defaultSeverity: 'severe' },
  { key: 'troponina-t', display: 'Troponina T', code: '6598-7', system: 'http://loinc.org', unit: 'ng/L', refLow: 0, refHigh: 14, anatomicalSystem: 'cardiovascular', defaultSeverity: 'severe' },
  { key: 'ck-mb', display: 'CK-MB', code: '13969-1', system: 'http://loinc.org', unit: 'ng/mL', refLow: 0, refHigh: 5, anatomicalSystem: 'cardiovascular' },
  { key: 'creatinina', display: 'Creatinina', code: '2160-0', system: 'http://loinc.org', unit: 'mg/dL', refLow: 0.6, refHigh: 1.3, anatomicalSystem: 'renal' },
  { key: 'bun', display: 'BUN / Urea', code: '3094-0', system: 'http://loinc.org', unit: 'mg/dL', refLow: 7, refHigh: 20, anatomicalSystem: 'renal' },
  { key: 'lactato', display: 'Lactato', code: '2524-7', system: 'http://loinc.org', unit: 'mmol/L', refLow: 0.5, refHigh: 2.2, anatomicalSystem: 'metabolic' },
  { key: 'wbc', display: 'Leucocitos', code: '6690-2', system: 'http://loinc.org', unit: 'x10³/µL', refLow: 4, refHigh: 11, anatomicalSystem: 'infectious' },
  { key: 'hb', display: 'Hemoglobina', code: '718-7', system: 'http://loinc.org', unit: 'g/dL', refLow: 12, refHigh: 17, anatomicalSystem: 'hematologic' },
  { key: 'pcr', display: 'Proteína C Reactiva (PCR)', code: '1988-5', system: 'http://loinc.org', unit: 'mg/L', refLow: 0, refHigh: 5, anatomicalSystem: 'infectious' },
  { key: 'potasio', display: 'Potasio', code: '2823-3', system: 'http://loinc.org', unit: 'mmol/L', refLow: 3.5, refHigh: 5.0, anatomicalSystem: 'metabolic' },
  { key: 'sodio', display: 'Sodio', code: '2951-2', system: 'http://loinc.org', unit: 'mmol/L', refLow: 135, refHigh: 145, anatomicalSystem: 'metabolic' },
  { key: 'inr', display: 'INR', code: '6301-6', system: 'http://loinc.org', unit: '', refLow: 0.9, refHigh: 1.2, anatomicalSystem: 'hematologic' },
  { key: 'glicemia-plasma', display: 'Glicemia en plasma', code: '2345-7', system: 'http://loinc.org', unit: 'mg/dL', refLow: 70, refHigh: 110, anatomicalSystem: 'metabolic' },
  { key: 'bicarbonato', display: 'Bicarbonato', code: '2028-9', system: 'http://loinc.org', unit: 'mmol/L', refLow: 22, refHigh: 29, anatomicalSystem: 'metabolic' },
  { key: 'ph-arterial', display: 'pH arterial', code: '2744-1', system: 'http://loinc.org', unit: '', refLow: 7.35, refHigh: 7.45, anatomicalSystem: 'metabolic' },
];

export const SYMPTOM_PRESETS: Preset[] = [
  { key: 'dolor-toracico', display: 'Dolor torácico', code: '29857009', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
  { key: 'disnea', display: 'Disnea', code: '267036007', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory' },
  { key: 'dolor-abdominal', display: 'Dolor abdominal', code: '21522001', system: 'http://snomed.info/sct', anatomicalSystem: 'gastrointestinal' },
  { key: 'cefalea', display: 'Cefalea', code: '25064002', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological' },
  { key: 'fiebre', display: 'Fiebre', code: '386661006', system: 'http://snomed.info/sct', anatomicalSystem: 'infectious' },
  { key: 'nauseas', display: 'Náuseas', code: '422587007', system: 'http://snomed.info/sct', anatomicalSystem: 'gastrointestinal' },
  { key: 'vomitos', display: 'Vómitos', code: '422400008', system: 'http://snomed.info/sct', anatomicalSystem: 'gastrointestinal' },
  { key: 'confusion', display: 'Confusión / desorientación', code: '40917007', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological' },
  { key: 'debilidad', display: 'Debilidad focal', code: '26544005', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological' },
  { key: 'palpitaciones', display: 'Palpitaciones', code: '80313002', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
  { key: 'sincope', display: 'Síncope', code: '271594007', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological' },
  { key: 'tos', display: 'Tos', code: '49727002', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory' },
  { key: 'disuria', display: 'Disuria', code: '49650001', system: 'http://snomed.info/sct', anatomicalSystem: 'renal' },
  { key: 'poliuria', display: 'Poliuria', code: '56842004', system: 'http://snomed.info/sct', anatomicalSystem: 'renal' },
];

export const IMAGING_PRESETS: Preset[] = [
  { key: 'ecg', display: 'ECG de 12 derivaciones', code: '11524-6', system: 'http://loinc.org', anatomicalSystem: 'cardiovascular' },
  { key: 'rx-torax', display: 'Radiografía de tórax', code: '36643-5', system: 'http://loinc.org', anatomicalSystem: 'respiratory' },
  { key: 'tac-cerebro', display: 'TAC de cerebro sin contraste', code: '24725-4', system: 'http://loinc.org', anatomicalSystem: 'neurological' },
  { key: 'tac-torax', display: 'TAC de tórax', code: '24627-2', system: 'http://loinc.org', anatomicalSystem: 'respiratory' },
  { key: 'eco-abdomen', display: 'Ecografía abdominal', code: '42397-9', system: 'http://loinc.org', anatomicalSystem: 'gastrointestinal' },
  { key: 'eco-renal', display: 'Ecografía renal', code: '24556-3', system: 'http://loinc.org', anatomicalSystem: 'renal' },
  { key: 'rx-abdomen', display: 'Radiografía de abdomen', code: '36627-8', system: 'http://loinc.org', anatomicalSystem: 'gastrointestinal' },
];

export const DIAGNOSIS_PRESETS: Preset[] = [
  { key: 'stemi', display: 'Infarto agudo de miocardio con elevación ST (STEMI)', code: '57054005', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular', defaultSeverity: 'life-threatening' },
  { key: 'nstemi', display: 'Infarto agudo sin elevación ST (NSTEMI)', code: '401314000', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular', defaultSeverity: 'severe' },
  { key: 'angina-inestable', display: 'Angina inestable', code: '4557003', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular', defaultSeverity: 'severe' },
  { key: 'sepsis', display: 'Sepsis', code: '91302008', system: 'http://snomed.info/sct', anatomicalSystem: 'infectious', defaultSeverity: 'life-threatening' },
  { key: 'shock-septico', display: 'Shock séptico', code: '76571007', system: 'http://snomed.info/sct', anatomicalSystem: 'infectious', defaultSeverity: 'life-threatening' },
  { key: 'epoc-exac', display: 'EPOC exacerbado', code: '195967001', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory', defaultSeverity: 'severe' },
  { key: 'asma-severa', display: 'Crisis asmática severa', code: '304527002', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory', defaultSeverity: 'severe' },
  { key: 'acv-isquemico', display: 'ACV isquémico', code: '422504002', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological', defaultSeverity: 'life-threatening' },
  { key: 'acv-hemorragico', display: 'ACV hemorrágico', code: '274100004', system: 'http://snomed.info/sct', anatomicalSystem: 'neurological', defaultSeverity: 'life-threatening' },
  { key: 'cad', display: 'Cetoacidosis diabética (CAD)', code: '420422005', system: 'http://snomed.info/sct', anatomicalSystem: 'metabolic', defaultSeverity: 'severe' },
  { key: 'apendicitis', display: 'Apendicitis aguda', code: '74400008', system: 'http://snomed.info/sct', anatomicalSystem: 'gastrointestinal', defaultSeverity: 'severe' },
  { key: 'tep', display: 'Tromboembolismo pulmonar', code: '59282003', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory', defaultSeverity: 'life-threatening' },
  { key: 'pielonefritis', display: 'Pielonefritis aguda', code: '45816000', system: 'http://snomed.info/sct', anatomicalSystem: 'renal', defaultSeverity: 'severe' },
];

export const HISTORY_PRESETS: Preset[] = [
  { key: 'hta', display: 'Hipertensión arterial', code: '38341003', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
  { key: 'dm2', display: 'Diabetes Mellitus tipo 2', code: '44054006', system: 'http://snomed.info/sct', anatomicalSystem: 'endocrine' },
  { key: 'dm1', display: 'Diabetes Mellitus tipo 1', code: '46635009', system: 'http://snomed.info/sct', anatomicalSystem: 'endocrine' },
  { key: 'tabaquismo', display: 'Tabaquismo activo', code: '77176002', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory' },
  { key: 'epoc-ant', display: 'EPOC previo', code: '13645005', system: 'http://snomed.info/sct', anatomicalSystem: 'respiratory' },
  { key: 'ic', display: 'Insuficiencia cardíaca', code: '42343007', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
  { key: 'fa', display: 'Fibrilación auricular', code: '49436004', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
  { key: 'dislipidemia', display: 'Dislipidemia', code: '55822004', system: 'http://snomed.info/sct', anatomicalSystem: 'metabolic' },
  { key: 'iam-previo', display: 'Infarto de miocardio previo', code: '22298006', system: 'http://snomed.info/sct', anatomicalSystem: 'cardiovascular' },
];

export function presetsFor(type: BuilderFinding['type']): Preset[] {
  switch (type) {
    case 'vital_sign': return VITAL_PRESETS;
    case 'laboratory': return LAB_PRESETS;
    case 'symptom': return SYMPTOM_PRESETS;
    case 'imaging': return IMAGING_PRESETS;
    case 'diagnosis': return DIAGNOSIS_PRESETS;
    case 'history': return HISTORY_PRESETS;
    default: return [];
  }
}

export const ANATOMICAL_SYSTEMS: AnatomicalSystem[] = [
  'cardiovascular', 'respiratory', 'neurological', 'gastrointestinal',
  'renal', 'metabolic', 'infectious', 'musculoskeletal', 'endocrine', 'hematologic',
];

export const TRIAGE_OPTIONS: Array<{ value: TriagePriority; label: string; color: string }> = [
  { value: 'immediate', label: 'INMEDIATO (rojo)', color: '#FF453A' },
  { value: 'delayed', label: 'PUEDE ESPERAR (ámbar)', color: '#FF9F0A' },
  { value: 'minor', label: 'AMBULATORIO (verde)', color: '#30D158' },
  { value: 'expectant', label: 'EXPECTANTE (gris)', color: '#8E8E93' },
  { value: 'deceased', label: 'FALLECIDO (negro)', color: '#3A3A3C' },
];

export const TYPE_LABELS: Record<BuilderFinding['type'], { label: string; icon: string; color: string }> = {
  vital_sign: { label: 'Signo vital', icon: '❤️', color: '#EF4444' },
  symptom: { label: 'Síntoma', icon: '🤕', color: '#EC4899' },
  laboratory: { label: 'Laboratorio', icon: '🧪', color: '#8B5CF6' },
  imaging: { label: 'Imagen', icon: '📷', color: '#06B6D4' },
  diagnosis: { label: 'Diagnóstico', icon: '🎯', color: '#F59E0B' },
  history: { label: 'Antecedente', icon: '📋', color: '#6B7280' },
};

// ═══════════════════════════════════════════════════════════════════
// Serialización: BuilderState → PatientGraph
// ═══════════════════════════════════════════════════════════════════

/**
 * Toma el estado del builder y arma un PatientGraph válido.
 * - Los IDs de nodos se auto-generan estables a partir del uiId.
 * - Si hay ≥1 diagnóstico, se crean relaciones implícitas 'causes' de cada
 *   hallazgo hacia el primer diagnóstico (facilita BFS de layout y render).
 * - `clinicalStatus` default: 'active'. Si el profesor quiere resolver algo,
 *   puede hacerlo con un second-pass manual (no se expone en v1 del builder).
 */
export function buildPatientGraph(state: BuilderState): PatientGraph {
  const now = new Date().toISOString();
  const idPrefix = `CASE-${state.slug.toUpperCase().replace(/[^A-Z0-9-]/g, '-')}`;

  const dxFinding = state.findings.find(f => f.type === 'diagnosis');
  const dxNodeId = dxFinding ? `node-${dxFinding.uiId}` : null;

  const nodes: ClinicalNodeData[] = state.findings.map(f => {
    const nodeId = `node-${f.uiId}`;
    const relationships = (dxNodeId && nodeId !== dxNodeId)
      ? [{ targetId: dxNodeId, type: 'causes' as const, strength: 0.7, bidirectional: false }]
      : [];

    const node: ClinicalNodeData = {
      id: nodeId,
      type: f.type,
      concept: {
        code: f.conceptCode ?? nodeId,
        display: f.conceptDisplay,
        system: f.conceptSystem ?? 'http://drnewenia.cloud/custom',
      },
      system: f.anatomicalSystem,
      clinicalStatus: 'active',
      severity: f.severity,
      physics: { mass: 1, volatility: 0.3, timeCriticality: 0.5 },
      visualization: { baseColor: '#2997FF', scale: 1 },
      relationships,
    };
    if (typeof f.numericValue === 'number' && f.unit !== undefined) {
      node.valueQuantity = { value: f.numericValue, unit: f.unit };
    }
    if (f.textValue && f.textValue.trim()) {
      node.valueString = f.textValue.trim();
    }
    if (typeof f.refLow === 'number' || typeof f.refHigh === 'number') {
      node.referenceRange = {};
      if (typeof f.refLow === 'number') node.referenceRange.low = f.refLow;
      if (typeof f.refHigh === 'number') node.referenceRange.high = f.refHigh;
    }
    return node;
  });

  return {
    id: idPrefix,
    demographics: {
      age: state.age,
      sex: state.sex,
      weight: state.weight,
    },
    triagePriority: state.triagePriority,
    chiefComplaint: state.chiefComplaint.trim() || undefined,
    nodes,
    lastUpdated: now,
  };
}

/**
 * Estado inicial del builder con un caso mínimo coherente (adulto, minor
 * priority, sin findings). El profesor arranca desde acá y agrega.
 */
export function emptyBuilderState(): BuilderState {
  return {
    slug: '',
    displayLabel: '',
    age: 50,
    sex: 'male',
    weight: undefined,
    chiefComplaint: '',
    triagePriority: 'delayed',
    findings: [],
  };
}

/**
 * Valida que el estado sea publicable. Devuelve el primer error encontrado
 * o null si todo OK. Reglas:
 *   - slug en formato válido
 *   - displayLabel no vacío
 *   - al menos 3 findings (para que la IA tenga contexto útil)
 *   - al menos 1 diagnóstico
 *   - cada finding con conceptDisplay
 */
export function validateBuilderState(state: BuilderState): string | null {
  if (!state.slug || !/^[a-z0-9][a-z0-9-]*$/.test(state.slug)) {
    return 'El slug es obligatorio y sólo permite minúsculas, dígitos y guiones.';
  }
  if (!state.displayLabel.trim()) return 'Falta el título visible del caso.';
  if (state.age < 0 || state.age > 120) return 'Edad fuera de rango (0-120).';
  if (state.findings.length < 3) return 'Agregá al menos 3 hallazgos clínicos.';
  if (!state.findings.some(f => f.type === 'diagnosis')) {
    return 'Agregá al menos 1 diagnóstico final.';
  }
  for (const f of state.findings) {
    if (!f.conceptDisplay.trim()) {
      return `Un hallazgo de tipo "${TYPE_LABELS[f.type].label}" no tiene nombre clínico.`;
    }
  }
  return null;
}

/**
 * Genera un uiId aleatorio + timestamp. No se serializa al backend — sólo
 * se usa como clave estable dentro del formulario para tracking de inputs
 * al agregar/remover findings.
 */
export function genUiId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Sugiere slug desde el displayLabel: lowercase, espacios → guiones,
 * quita acentos. El profesor puede sobrescribir.
 */
export function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
}
